import { browserFingerprintService } from './browserFingerprint';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentData {
  preferences: CookiePreferences;
  timestamp: number;
  browserFingerprint: string;
  version: string;
}

class CookieManager {
  private static instance: CookieManager;
  private preferences: CookiePreferences = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  };

  private constructor() {
    this.loadPreferences();
  }

  public static getInstance(): CookieManager {
    if (!CookieManager.instance) {
      CookieManager.instance = new CookieManager();
    }
    return CookieManager.instance;
  }

  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem('cookie-consent');
      if (stored) {
        const data: CookieConsentData = JSON.parse(stored);
        
        // 检查版本兼容性
        if (data.version !== '2.0') {
          console.log('Cookie consent data version mismatch, resetting...');
          this.resetPreferences();
          return;
        }

        // 检查浏览器指纹是否匹配
        const currentFingerprint = browserFingerprintService.getFingerprint().hash;
        if (data.browserFingerprint !== currentFingerprint) {
          console.log('Browser fingerprint mismatch, showing consent banner...');
          // 不加载偏好设置，让用户重新选择
          return;
        }

        this.preferences = data.preferences;
      }
    } catch (error) {
      console.error('Error loading cookie preferences:', error);
    }
  }

  public getPreferences(): CookiePreferences {
    return { ...this.preferences };
  }

  public setPreferences(preferences: CookiePreferences): void {
    this.preferences = { ...preferences };
    
    // 生成并存储浏览器指纹
    const fingerprint = browserFingerprintService.getFingerprint();
    browserFingerprintService.storeFingerprint();
    
    const data: CookieConsentData = {
      preferences: this.preferences,
      timestamp: Date.now(),
      browserFingerprint: fingerprint.hash,
      version: '2.0',
    };
    localStorage.setItem('cookie-consent', JSON.stringify(data));
  }

  public hasConsent(): boolean {
    try {
      const stored = localStorage.getItem('cookie-consent');
      if (!stored) return false;

      const data: CookieConsentData = JSON.parse(stored);
      
      // 检查版本兼容性
      if (data.version !== '2.0') return false;

      // 检查浏览器指纹是否匹配
      const currentFingerprint = browserFingerprintService.getFingerprint().hash;
      return data.browserFingerprint === currentFingerprint;
    } catch (error) {
      console.error('Error checking consent:', error);
      return false;
    }
  }

  public canUseAnalytics(): boolean {
    return this.preferences.analytics;
  }

  public canUseMarketing(): boolean {
    return this.preferences.marketing;
  }

  public canUseFunctional(): boolean {
    return this.preferences.functional;
  }

  public canUseNecessary(): boolean {
    return this.preferences.necessary;
  }

  // Google Analytics
  public initializeGoogleAnalytics(measurementId: string): void {
    if (!this.canUseAnalytics()) return;

    // 动态加载Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // 初始化gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    gtag('js', new Date());
    gtag('config', measurementId, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
    });

    // 将gtag函数添加到window对象
    (window as any).gtag = gtag;
  }

  // 设置Cookie
  public setCookie(name: string, value: string, options: {
    expires?: number;
    path?: string;
    domain?: string;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
    category?: keyof CookiePreferences;
  } = {}): void {
    const { category = 'necessary', ...cookieOptions } = options;

    // 检查是否有权限设置此类型的Cookie
    if (!this.preferences[category]) {
      console.warn(`Cannot set ${category} cookie: user has not consented`);
      return;
    }

    let cookieString = `${name}=${value}`;

    if (cookieOptions.expires) {
      const date = new Date();
      date.setTime(date.getTime() + cookieOptions.expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (cookieOptions.path) {
      cookieString += `; path=${cookieOptions.path}`;
    }

    if (cookieOptions.domain) {
      cookieString += `; domain=${cookieOptions.domain}`;
    }

    if (cookieOptions.secure) {
      cookieString += `; secure`;
    }

    if (cookieOptions.sameSite) {
      cookieString += `; samesite=${cookieOptions.sameSite}`;
    }

    document.cookie = cookieString;
  }

  // 获取Cookie
  public getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // 删除Cookie
  public deleteCookie(name: string, path: string = '/', domain?: string): void {
    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    if (domain) {
      cookieString += `; domain=${domain}`;
    }
    document.cookie = cookieString;
  }

  // 清除所有非必要Cookie
  public clearNonEssentialCookies(): void {
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      // 保留必要的Cookie，删除其他
      if (!this.isNecessaryCookie(name)) {
        this.deleteCookie(name);
      }
    });
  }

  private isNecessaryCookie(name: string): boolean {
    // 定义必要Cookie的名称模式
    const necessaryPatterns = [
      'cookie-consent',
      'session',
      'csrf',
      'auth',
      'security',
    ];
    
    return necessaryPatterns.some(pattern => 
      name.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  // 跟踪事件（仅在用户同意分析Cookie时）
  public trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.canUseAnalytics()) return;

    if ((window as any).gtag) {
      (window as any).gtag('event', eventName, parameters);
    }
  }

  // 跟踪页面浏览（仅在用户同意分析Cookie时）
  public trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.canUseAnalytics()) return;

    if ((window as any).gtag) {
      (window as any).gtag('config', 'GA_MEASUREMENT_ID', {
        page_path: pagePath,
        page_title: pageTitle,
      });
    }
  }

  // 重置所有偏好设置
  public resetPreferences(): void {
    localStorage.removeItem('cookie-consent');
    browserFingerprintService.clearFingerprints();
    this.preferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
  }

  // 获取浏览器指纹信息
  public getBrowserInfo(): {
    isKnownBrowser: boolean;
    fingerprint: string;
    storedCount: number;
  } {
    const fingerprint = browserFingerprintService.getFingerprint();
    const isKnown = browserFingerprintService.isKnownBrowser();
    const storedCount = browserFingerprintService.getStoredFingerprints().length;

    return {
      isKnownBrowser: isKnown,
      fingerprint: fingerprint.hash,
      storedCount,
    };
  }
}

// 导出单例实例
export const cookieManager = CookieManager.getInstance();
export type { CookiePreferences };
